// Dragging to-dos with a mouse (MacDragDrop.swift): within a list to reorder them, onto a section, onto a day in
// Scheduled, and onto Home's lists, areas and projects, with a label of what the drop does. Due, the Archive, Stale
// and Find refuse: their order is not a hand-made one, and dropping there would mean nothing.

import type { DragEvent } from "react";
import { offersPlaceBeside, type Route } from "../core/lists";
import type { Todo } from "../core/records";
import { addDays } from "../core/days";
import { t } from "../i18n";
import type { Window } from "./context";
import { routeTitle } from "./format";
import { arrangementFor } from "./prefs";

let dragging: readonly string[] | null = null;
let label: HTMLDivElement | null = null;

const MIME = "application/x-todolix-todos";

function showLabel(text: string | null, event: DragEvent) {
  if (!label) {
    label = document.createElement("div");
    label.className = "drop-label";
    label.setAttribute("role", "status");
    document.body.append(label);
  }
  label.hidden = text === null;
  if (text !== null) {
    label.textContent = text;
    label.style.left = `${event.clientX + 16}px`;
    label.style.top = `${event.clientY + 14}px`;
  }
}

/** The to-dos in the hand, while a drag of them is on. */
export function draggedIDs(): readonly string[] | null {
  return dragging;
}

function endDrag() {
  dragging = null;
  kept = null;
  if (label) label.hidden = true;
  document.querySelectorAll(".drop-before, .drop-after, .drop-into").forEach((element) => element.classList.remove("drop-before", "drop-after", "drop-into"));
  document.body.classList.remove("dragging");
}

/** Refused as a place to drop on, and inside: lists whose order isn't made by hand. */
export function refusesDrops(route: Route): boolean {
  return route.kind === "due" || route.kind === "archive" || route.kind === "stale" || route.kind === "search";
}

/** What a drop on a list of Home does, in words; null when it refuses. */
function dropAction(window: Window, route: Route, count: number): { label: string; run: (ids: readonly string[]) => void } | null {
  const { model, state } = window;
  const name = count === 1 ? "" : ` (${count})`;
  switch (route.kind) {
    case "inbox": return { label: t("Move to Inbox") + name, run: (ids) => model.move(ids, { kind: "inbox" }) };
    case "today": return { label: t("Schedule for Today") + name, run: (ids) => model.schedule(ids, { kind: "on", day: state.today }) };
    case "scheduled": {
      const tomorrow = addDays(state.today, 1);
      return { label: t("Schedule for Tomorrow") + name, run: (ids) => model.schedule(ids, { kind: "on", day: tomorrow }) };
    }
    case "anytime": return { label: t("Move to Anytime") + name, run: (ids) => model.schedule(ids, { kind: "anytime" }) };
    case "someday": return { label: t("Move to Someday") + name, run: (ids) => model.schedule(ids, { kind: "someday" }) };
    case "general": return { label: t("Move to Miscellaneous") + name, run: (ids) => model.move(ids, { kind: "general" }) };
    case "project": case "area": {
      const title = routeTitle(route, state.lists);
      return { label: t("Move to {name}", { name: title }) + name, run: (ids) => model.move(ids, route) };
    }
    default: return null;
  }
}


/** A row that can be dragged: the selection when it holds the row, else the row alone. */
export function rowDragProps(window: Window, todo: Todo) {
  if (window.layout.touch) return {};
  return {
    draggable: true,
    onDragStart: (event: DragEvent) => {
      const ids = window.ui.selected.includes(todo.id) ? window.ui.selected : [todo.id];
      dragging = ids;
      kept = null;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(MIME, ids.join(","));
      event.dataTransfer.setData("text/plain", window.state.records.todos.filter((other) => ids.includes(other.id)).map((other) => other.title).join("\n"));
      document.body.classList.add("dragging");
    },
    onDragEnd: endDrag,
  };
}

function accept(event: DragEvent, allowed: boolean): boolean {
  if (!dragging) return false;
  if (!allowed) {
    // Refused here: the list's ground doesn't take it either.
    (event.nativeEvent as MarkedEvent).todolixRefused = true;
    event.dataTransfer.dropEffect = "none";
    return false;
  }
  event.preventDefault();
  event.dataTransfer.dropEffect = "move";
  return true;
}

/** A list, area or project on Home that to-dos can be dropped on. */
export function dropProps(window: Window, route: Route) {
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragging) return;
      const action = dropAction(window, route, dragging.length);
      if (!accept(event, action !== null)) {
        showLabel(null, event);
        return;
      }
      event.currentTarget.classList.add("drop-into");
      showLabel(action?.label ?? null, event);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      event.currentTarget.classList.remove("drop-into");
      showLabel(null, event);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      const ids = dragging;
      const action = ids && dropAction(window, route, ids.length);
      endDrag();
      if (!ids || !action) return;
      event.preventDefault();
      action.run(ids);
      window.set({ selected: [] });
      window.showToast(action.label, true);
    },
  };
}

/** A row of a list as a place to drop on: above or below it, by the half the pointer is in. */
export function rowDropProps(window: Window, todo: Todo, inToday: boolean, onPlace?: (ids: readonly string[], where: "before" | "after") => void) {
  const route = window.ui.finding ? { kind: "search", query: "" } as const : window.ui.route;
  // What the list places by its dates (a deadline under Deadlines First, a start under the "Later" line) is no place
  // to drop, and a drag with one of it has no place in the list (ListArrangement.placesByDate). The Day view plans.
  const arrangement = arrangementFor(route);
  // Nor, for open to-dos, among the completed ones at the end (ListArrangement.offersPlace(beside:dragging:); Bastian,
  // 2026-10-29): a ticked one stands anywhere in the user's order, so a drop beside it landed rows away.
  const lingering = { has: (id: string) => window.state.lists.isLingering(id) };
  const refusedFor = (ids: readonly string[]) => {
    if (refusesDrops(route)) return true;
    if (onPlace) return false;
    const dragged = window.state.records.todos.filter((other) => ids.includes(other.id));
    return !offersPlaceBeside(todo, dragged, arrangement, window.state.today, lingering);
  };
  const place = (event: DragEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return event.clientY < box.top + box.height / 2 ? "before" : "after";
  };
  const land = (ids: readonly string[], where: "before" | "after") => {
    if (refusedFor(ids) || ids.includes(todo.id)) return;
    // The Day view plans what is dropped on a row, rather than moving it in the list.
    if (onPlace) {
      onPlace(ids, where);
      window.set({ selected: [] });
      return;
    }
    // Dropped onto a day of Scheduled: that day first, then the place among its to-dos.
    if (route.kind === "scheduled" && todo.start.kind === "on") {
      const day = todo.start.day;
      const off = window.state.records.todos.filter((other) => ids.includes(other.id) && !(other.start.kind === "on" && other.start.day === day));
      if (off.length) window.model.schedule(off.map((other) => other.id), { kind: "on", day });
    }
    window.model.reorder(ids, todo.id, where, inToday);
  };
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragging || dragging.includes(todo.id)) return;
      if (!accept(event, !refusedFor(dragging))) return;
      const where = place(event);
      keep(event, (ids) => land(ids, where));
      event.currentTarget.classList.toggle("drop-before", where === "before");
      event.currentTarget.classList.toggle("drop-after", where === "after");
      showLabel(null, event);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => unmark(event, "drop-before", "drop-after"),
    onDrop: (event: DragEvent<HTMLElement>) => {
      const ids = dragging;
      const where = place(event);
      endDrag();
      if (!ids || refusedFor(ids) || ids.includes(todo.id)) return;
      event.preventDefault();
      land(ids, where);
    },
  };
}

// MARK: - The week (docs/56): a day's column and the tabs of "Unscheduled" take to-dos, before a row or at the end.

/**
 * A column of the week or of "Unscheduled": what is dropped on it (outside its rows) goes to its end. `markOn` names
 * the element that shows where it lands, when not the column itself (a group of "Unscheduled" a to-do won't land in
 * marks the one it will; null marks none).
 */
export function columnDropProps(run: (ids: readonly string[]) => void, options: { markOn?: (column: HTMLElement) => HTMLElement | null } = {}) {
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!accept(event, true)) return;
      const mark = options.markOn ? options.markOn(event.currentTarget) : event.currentTarget;
      keep(event, run, mark ?? event.currentTarget);
      mark?.classList.add("drop-into");
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
      unmark(event, "drop-into");
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      const ids = dragging;
      endDrag();
      if (!ids) return;
      event.preventDefault();
      run(ids);
    },
  };
}

/**
 * A row in a column of the week: the drop lands above it (upper half) or above the next one (`next`, or the end).
 * Where `takes` says no (a group of "Unscheduled" the to-do won't land in), the row leaves the drag to its column.
 */
export function columnRowDropProps(todo: Todo, next: string | undefined, run: (ids: readonly string[], before: string | undefined) => void, takes: () => boolean = () => true) {
  const place = (event: DragEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return event.clientY < box.top + box.height / 2 ? "before" : "after";
  };
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragging || dragging.includes(todo.id) || !takes()) return;
      if (!accept(event, true)) return;
      event.stopPropagation();
      event.currentTarget.closest(".drop-into")?.classList.remove("drop-into");
      const where = place(event);
      keep(event, (ids) => {
        if (!ids.includes(todo.id)) run(ids, where === "before" ? todo.id : next);
      });
      event.currentTarget.classList.toggle("drop-before", where === "before");
      event.currentTarget.classList.toggle("drop-after", where === "after");
      showLabel(null, event);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => unmark(event, "drop-before", "drop-after"),
    onDrop: (event: DragEvent<HTMLElement>) => {
      if (!takes()) return;
      const ids = dragging;
      const where = place(event);
      event.stopPropagation();
      endDrag();
      if (!ids || ids.includes(todo.id)) return;
      event.preventDefault();
      run(ids, where === "before" ? todo.id : next);
    },
  };
}

/**
 * A section's heading (a project's or Today's), a board's column or a day in Scheduled: the to-dos go into it. `mark`
 * is what shows where: the heading highlighted ("drop-into"), or a line under it ("drop-after": a list's section, whose
 * top the to-dos go to).
 */
export function headerDropProps(window: Window, run: (ids: readonly string[]) => void, mark: "drop-into" | "drop-after" = "drop-into") {
  const land = (ids: readonly string[]) => {
    run(ids);
    window.set({ selected: [] });
  };
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!accept(event, true)) return;
      keep(event, land);
      event.currentTarget.classList.add(mark);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => unmark(event, mark),
    onDrop: (event: DragEvent<HTMLElement>) => {
      const ids = dragging;
      endDrag();
      if (!ids) return;
      event.preventDefault();
      land(ids);
    },
  };
}

// MARK: - The place that stays (docs/59, item 2; the Mac's MacListGroundDropDelegate and MacDragInHand)
//
// Over the room between places (the list's heading, a gap between rows, sections or columns, the room beside and under
// a board or the week) the place last marked keeps its mark, and letting go lands the to-dos there, as that place would
// have. Before, nothing took the drop there (the pointer showed it was refused) and the to-do stayed where it was. The
// mark moves only when another place takes over; it goes when the drag ends, or 80 ms after it left the list.

type MarkedEvent = globalThis.Event & { todolixKept?: boolean; todolixRefused?: boolean };

const MARKS = ["drop-before", "drop-after", "drop-into"];
let kept: { element: HTMLElement; land: (ids: readonly string[]) => void } | null = null;
/** Counts the drag's reports over the list: one leaving it clears the mark only if none came within 80 ms. */
let reports = 0;

/** A place under the pointer marks itself: the innermost place of one event is the one a drop in the room lands in. */
function keep(event: DragEvent<HTMLElement>, land: (ids: readonly string[]) => void, element: HTMLElement = event.currentTarget): void {
  reports += 1;
  const native = event.nativeEvent as MarkedEvent;
  if (native.todolixKept) return;
  native.todolixKept = true;
  if (kept && kept.element !== element) kept.element.classList.remove(...MARKS);
  kept = { element, land };
}

/** The pointer leaves a place: its mark goes, unless it is the place kept, which stays until another takes over. */
function unmark(event: DragEvent<HTMLElement>, ...classes: string[]): void {
  if (dragging && kept?.element === event.currentTarget) return;
  event.currentTarget.classList.remove(...classes);
}

/** The list's ground (its `<main>`): the room between the places that take to-dos keeps the last one and lands there. */
export function groundDropProps() {
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!dragging) return;
      reports += 1;
      const native = event.nativeEvent as MarkedEvent;
      // A place took it, or refused it; or there is no place to keep.
      if (event.defaultPrevented || native.todolixRefused || !kept) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
      showLabel(null, event);
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => {
      if (!dragging || !kept) return;
      const ground = event.currentTarget;
      const to = event.relatedTarget;
      if (to instanceof Node && ground.contains(to)) return;
      // Without a related element (a finger, some browsers), the pointer's place says whether it left.
      if (!(to instanceof Node)) {
        const box = ground.getBoundingClientRect();
        const inside = (event.clientX !== 0 || event.clientY !== 0)
          && event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (inside) return;
      }
      const seen = reports;
      setTimeout(() => {
        if (reports !== seen || !kept) return;
        kept.element.classList.remove(...MARKS);
        kept = null;
      }, 80);
    },
    onDrop: (event: DragEvent<HTMLElement>) => {
      // A place landed it already (the drag is over), or there is nothing to land in.
      const ids = dragging;
      const place = kept;
      if (!ids || !place) return;
      event.preventDefault();
      endDrag();
      place.land(ids);
    },
  };
}

// MARK: - Moving projects, areas and sections (MacSidebar.swift, MacSectionReordering.swift)

/** A project, an area or a section being moved to another place by hand. */
export type Place = { kind: "project"; id: string } | { kind: "area"; id: string } | { kind: "section"; id: string };

let movingPlace: Place | null = null;
const PLACE_MIME = "application/x-todolix-place";

function endPlaceDrag() {
  movingPlace = null;
  document.querySelectorAll(".drop-before, .drop-after, .drop-into").forEach((element) => element.classList.remove("drop-before", "drop-after", "drop-into"));
  document.body.classList.remove("dragging");
}

/** What can be picked up and moved: with a mouse, as on the Mac. */
export function placeDragProps(window: Window, place: Place) {
  if (window.layout.touch) return {};
  return {
    draggable: true,
    onDragStart: (event: DragEvent) => {
      event.stopPropagation();
      movingPlace = place;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(PLACE_MIME, `${place.kind}:${place.id}`);
      document.body.classList.add("dragging");
    },
    onDragEnd: endPlaceDrag,
  };
}

/** The id that comes right after `target` among `siblings` (in their order, without what moves): where "below it" is. */
function after(siblings: readonly { id: string }[], target: string, moving: string): string | undefined {
  const others = siblings.filter((sibling) => sibling.id !== moving);
  const index = others.findIndex((sibling) => sibling.id === target);
  return index === -1 ? undefined : others[index + 1]?.id;
}

/**
 * Where a moved project, area or section lands: above or below `target` (by the half the pointer is in), or into an
 * area's heading for a project. `accepts` says which kinds this place takes; `land` does the move.
 */
export function placeDropProps(target: Place, accepts: (moving: Place) => "around" | "into" | null, land: (moving: Place, where: "before" | "after" | "into") => void) {
  const where = (event: DragEvent<HTMLElement>, how: "around" | "into"): "before" | "after" | "into" => {
    if (how === "into") return "into";
    const box = event.currentTarget.getBoundingClientRect();
    return event.clientY < box.top + box.height / 2 ? "before" : "after";
  };
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => {
      if (!movingPlace || (movingPlace.kind === target.kind && movingPlace.id === target.id)) return;
      const how = accepts(movingPlace);
      if (!how) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      const place = where(event, how);
      event.currentTarget.classList.toggle("drop-before", place === "before");
      event.currentTarget.classList.toggle("drop-after", place === "after");
      event.currentTarget.classList.toggle("drop-into", place === "into");
    },
    onDragLeave: (event: DragEvent<HTMLElement>) => event.currentTarget.classList.remove("drop-before", "drop-after", "drop-into"),
    onDrop: (event: DragEvent<HTMLElement>) => {
      const moving = movingPlace;
      const how = moving ? accepts(moving) : null;
      if (!moving || !how) return;
      const place = where(event, how);
      event.preventDefault();
      event.stopPropagation();
      endPlaceDrag();
      if (moving.kind === target.kind && moving.id === target.id) return;
      land(moving, place);
    },
  };
}

/** Handlers for a place that also takes to-dos: both kinds of drag, each answered by its own. */
type DropHandlers = Partial<Record<"onDragOver" | "onDragLeave" | "onDrop", (event: DragEvent<HTMLElement>) => void>>;

export function bothDropProps(...sets: DropHandlers[]) {
  return {
    onDragOver: (event: DragEvent<HTMLElement>) => { for (const set of sets) set.onDragOver?.(event); },
    onDragLeave: (event: DragEvent<HTMLElement>) => { for (const set of sets) set.onDragLeave?.(event); },
    onDrop: (event: DragEvent<HTMLElement>) => { for (const set of sets) set.onDrop?.(event); },
  };
}

export { after as placeAfter };

// MARK: - Dragging with a finger (ListScreen.swift: a hold lifts the row, then it follows the finger)
//
// Touch has no HTML drag and drop, so the finger's drag sends the same drag events the mouse would, to whatever is
// under it: every place that takes a drop answers as it does for the mouse.

let touchTarget: Element | null = null;
let ghost: HTMLElement | null = null;
let ghostOffset = { x: 0, y: 0 };
let lastPoint = { x: 0, y: 0 };
let scrolling = 0;

function send(type: "dragover" | "dragleave" | "drop", target: Element, x: number, y: number): void {
  let transfer: DataTransfer | undefined;
  try {
    transfer = new DataTransfer();
  } catch {
    transfer = undefined;
  }
  const init = { bubbles: true, cancelable: true, clientX: x, clientY: y, ...(transfer ? { dataTransfer: transfer } : {}) };
  target.dispatchEvent(new DragEvent(type, init));
}

/** Lifts `ids` under the finger: `row` is drawn in the hand. */
export function beginTouchDrag(ids: readonly string[], row: HTMLElement, x: number, y: number): void {
  dragging = ids;
  kept = null;
  document.body.classList.add("dragging");
  const box = row.getBoundingClientRect();
  ghost = row.cloneNode(true) as HTMLElement;
  ghost.classList.add("touch-ghost");
  ghost.removeAttribute("data-row");
  ghost.style.width = `${box.width}px`;
  ghostOffset = { x: x - box.left, y: y - box.top };
  document.body.append(ghost);
  row.classList.add("lifted");
  moveTouchDrag(x, y);
  const scroll = () => {
    if (!dragging) return;
    // Near the top or bottom of the list under the finger, the list scrolls on its own; a board (which scrolls itself,
    // both ways) also near its left or right edge.
    const container = document.elementFromPoint(lastPoint.x, lastPoint.y)?.closest(".board-body > .board, .main, .sidebar");
    if (container) {
      const box = container.getBoundingClientRect();
      const edge = 70;
      if (lastPoint.y < box.top + edge) container.scrollTop -= 8;
      else if (lastPoint.y > box.bottom - edge) container.scrollTop += 8;
      if (container.classList.contains("board")) {
        if (lastPoint.x < box.left + edge) container.scrollLeft -= 8;
        else if (lastPoint.x > box.right - edge) container.scrollLeft += 8;
      }
    }
    scrolling = requestAnimationFrame(scroll);
  };
  scrolling = requestAnimationFrame(scroll);
}

export function moveTouchDrag(x: number, y: number): void {
  lastPoint = { x, y };
  if (ghost) ghost.style.transform = `translate(${x - ghostOffset.x}px, ${y - ghostOffset.y}px)`;
  const target = document.elementFromPoint(x, y);
  if (touchTarget && touchTarget !== target) send("dragleave", touchTarget, x, y);
  touchTarget = target;
  if (target) send("dragover", target, x, y);
}

/** Lets go: whatever is under the finger takes the drop, as it would from the mouse. */
export function endTouchDrag(drop: boolean): void {
  cancelAnimationFrame(scrolling);
  const target = drop ? document.elementFromPoint(lastPoint.x, lastPoint.y) : null;
  if (target) send("drop", target, lastPoint.x, lastPoint.y);
  ghost?.remove();
  ghost = null;
  touchTarget = null;
  document.querySelectorAll(".lifted").forEach((element) => element.classList.remove("lifted"));
  endDrag();
}
